import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SIDEBAR_PAST_GATHERINGS_DAYS } from './config';
import './GatheringsSidebar.css';
import UserDetail from './UserDetail';
import DescriptionOrg from './DescriptionOrg';
import { eventsAPI, communityEventsAPI, badgesAPI, picturesAPI } from './services/api';
import { parseUTCDate, formatDateLong, formatTime, isEventPast } from './utils/dateUtils';
import { buildGroupPhotoExistsMap } from './utils/pictureUtils';
import ReactDialog from './ReactDialog';
import ParticipantsDialog from './ParticipantsDialog';
import SendStarDialog from './SendStarDialog';
import EventGalleryModal from './EventGalleryModal';
import PictureUploadModal from './PictureUploadModal';
import InviteFriendsDialog from './InviteFriendsDialog';
import { useTranslation } from 'react-i18next';

function GatheringsSidebar({ currentUser }) {
  const { t, i18n } = useTranslation();
  const [myGatherings, setMyGatherings] = useState([]);
  const [followedEvents, setFollowedEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showIncludeDialog, setShowIncludeDialog] = useState(false);
  const [includeEventId, setIncludeEventId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addingParticipants, setAddingParticipants] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [eventFilter, setEventFilter] = useState('both'); // 'both', 'attending', 'following', 'past'
  const [showReactDialog, setShowReactDialog] = useState(false);
  const [reactDialogEvent, setReactDialogEvent] = useState(null);
  const [reactDialogTitle, setReactDialogTitle] = useState('');
  const [loadingGathering, setLoadingGathering] = useState(false);
  const [giveawayStars, setGiveawayStars] = useState(0);
  const [givenStars, setGivenStars] = useState({});
  const [showStarModal, setShowStarModal] = useState(false);
  const [selectedStarRecipient, setSelectedStarRecipient] = useState(null);
  const [showEventGallery, setShowEventGallery] = useState(false);
  const [eventGalleryPictures, setEventGalleryPictures] = useState([]);
  const [galleryEvent, setGalleryEvent] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [existingPictures, setExistingPictures] = useState({});
  const [groupPhotoEventId, setGroupPhotoEventId] = useState(null); // non-null = uploading group event photo
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [groupPhotoExists, setGroupPhotoExists] = useState({});
  const [mapAddress, setMapAddress] = useState(null);
  const [groupPhotoEvent, setGroupPhotoEvent] = useState(null);
  const [includeEventFull, setIncludeEventFull] = useState(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (!currentUser) return;

    // Load user's gatherings and followed events from API
    const loadGatherings = async () => {
      try {
        setLoading(true);

        // Load user events, followed community events, past events, and stars in parallel
        const [events, followed, past, starsCount] = await Promise.all([
          eventsAPI.getUserEvents(currentUser.id),
          communityEventsAPI.getUserFollowed(currentUser.id),
          eventsAPI.getUserPastEvents(currentUser.id, SIDEBAR_PAST_GATHERINGS_DAYS),
          badgesAPI.getGiveawayCount(currentUser.id)
        ]);

        const upcomingEvents = events.filter(event => !isEventPast(event.dateTime, event.isEnded));

        setMyGatherings(upcomingEvents);
        setFollowedEvents(followed);
        setPastEvents(past);
        setGiveawayStars(starsCount);

        // Check if organizer already uploaded a group photo for each event they organize
        const photoMap = await buildGroupPhotoExistsMap(
          [...upcomingEvents, ...past],
          currentUser.id
        );
        setGroupPhotoExists(photoMap);
      } catch (error) {
        console.error('Error loading gatherings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGatherings();

    // Refresh when user joins an event from the main page
    const handleRefresh = () => loadGatherings();
    window.addEventListener('myGatheringsRefresh', handleRefresh);
    return () => window.removeEventListener('myGatheringsRefresh', handleRefresh);
  }, [currentUser]);

  const handleConfirm = async (eventId) => {
    try {
      await eventsAPI.updateParticipationStatus(eventId, 'confirmed');

      // Update local state
      setMyGatherings(prevGatherings =>
        prevGatherings.map(event => {
          if (event.id === eventId) {
            return { ...event, userParticipantStatus: 1 };
          }
          return event;
        })
      );
    } catch (error) {
      console.error('Error confirming event:', error);
      alert(t('gatheringsSidebar.alerts.failedConfirm'));
    }
  };

  const handleDecline = async (eventId) => {
    try {
      await eventsAPI.updateParticipationStatus(eventId, 'declined');

      // Remove the event from the list
      setMyGatherings(prevGatherings =>
        prevGatherings.filter(event => event.id !== eventId)
      );
    } catch (error) {
      console.error('Error declining event:', error);
      alert(t('gatheringsSidebar.alerts.failedDecline'));
    }
  };

  // Helper function to normalize status (backend returns numbers, frontend uses strings)
  const normalizeStatus = (status) => {
    if (status === 1 || status === 'confirmed') return 'confirmed';
    if (status === -1 || status === 'declined') return 'declined';
    return 'pending';
  };

  const getParticipantStatus = (event) => {
    return normalizeStatus(event.userParticipantStatus);
  };

  const isOrganizer = (event) => {
    return event.organizer.id === currentUser.id;
  };

  const isOngoing = (event) => {
    const now = new Date();
    const eventDateTime = parseUTCDate(event.dateTime);

    const twoHoursAfterStart = new Date(eventDateTime);
    twoHoursAfterStart.setHours(twoHoursAfterStart.getHours() + 2);

    // Event is ongoing if it has started but less than 2 hours have passed
    return eventDateTime <= now && now <= twoHoursAfterStart;
  };

  const showParticipants = async (event) => {
    try {
      const fullEvent = await eventsAPI.getById(event.id);
      setSelectedEvent(fullEvent);
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  const showFollowedEventParticipants = async (event) => {
    if (!event.autoCreatedEventId) return;
    try {
      const fullEvent = await eventsAPI.getById(event.autoCreatedEventId);
      setSelectedEvent(fullEvent);
    } catch (error) {
      console.error('Error loading participants:', error);
    }
  };

  const closeParticipantsDialog = () => {
    setSelectedEvent(null);
  };

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
  };

  const handleCloseUserDetail = () => {
    setSelectedUserId(null);
  };

  const handleEndGathering = async (eventId) => {
    if (!window.confirm(t('gatheringsSidebar.confirms.endGathering'))) {
      return;
    }

    try {
      await eventsAPI.endGathering(eventId);

      // Remove the ended event from the list
      setMyGatherings(prevGatherings =>
        prevGatherings.filter(event => event.id !== eventId)
      );

      alert(t('gatheringsSidebar.alerts.ended'));
    } catch (error) {
      console.error('Error ending gathering:', error);
      alert(t('gatheringsSidebar.alerts.failedEnd'));
    }
  };

  const handleLeaveEvent = async (eventId) => {
    if (!window.confirm(t('gatheringsSidebar.confirms.leaveEvent'))) {
      return;
    }

    try {
      await eventsAPI.leaveEvent(eventId);

      // Remove the event from the list
      setMyGatherings(prevGatherings =>
        prevGatherings.filter(event => event.id !== eventId)
      );

      alert(t('gatheringsSidebar.alerts.left'));
    } catch (error) {
      console.error('Error leaving event:', error);
      alert(t('gatheringsSidebar.alerts.failedLeave'));
    }
  };

  const handleUnfollow = async (eventId) => {
    // Show confirmation dialog
    const confirmed = window.confirm(t('gatheringsSidebar.confirms.unfollowEvent'));

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      await communityEventsAPI.unfollow(eventId);

      // Remove from followed events list
      setFollowedEvents(prev => prev.filter(event => event.id !== eventId));
    } catch (error) {
      console.error('Error unfollowing event:', error);
      alert(t('gatheringsSidebar.alerts.failedUnfollow'));
    }
  };

  const isUpcoming = (event) => {
    const now = new Date();
    const eventDateTime = parseUTCDate(event.dateTime);

    // Event is upcoming if it hasn't started yet
    return eventDateTime > now;
  };

  const openIncludeDialog = async (eventId) => {
    setIncludeEventId(eventId);
    setSelectedUsers([]);
    setShowIncludeDialog(true);
    try {
      const full = await eventsAPI.getById(eventId);
      setIncludeEventFull(full);
    } catch (error) {
      console.error('Error loading event participants:', error);
    }
  };

  const closeIncludeDialog = () => {
    setShowIncludeDialog(false);
    setIncludeEventId(null);
    setIncludeEventFull(null);
    setSelectedUsers([]);
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddParticipants = async () => {
    if (selectedUsers.length === 0) {
      alert(t('gatheringsSidebar.alerts.selectPerson'));
      return;
    }

    try {
      setAddingParticipants(true);
      const result = await eventsAPI.addParticipants(includeEventId, selectedUsers);

      if (result.addedCount > 0) {
        // Reload the event to get updated participants
        const events = await eventsAPI.getUserEvents(currentUser.id);
        setMyGatherings(events.filter(event => !isEventPast(event.dateTime, event.isEnded)));

        alert(t('gatheringsSidebar.alerts.added', { count: result.addedCount }));
      } else {
        alert(t('gatheringsSidebar.alerts.noNewParticipants'));
      }

      closeIncludeDialog();
    } catch (error) {
      console.error('Error adding participants:', error);
      alert(t('gatheringsSidebar.alerts.failedAddParticipants'));
    } finally {
      setAddingParticipants(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const hasPendingInvites = myGatherings.some(
    e => !isOrganizer(e) && normalizeStatus(e.userParticipantStatus) === 'pending'
  );

  const openReactDialog = async (event, title) => {
    setReactDialogTitle(title);
    setShowReactDialog(true);
    setLoadingGathering(true);

    let fullEvent = event;
    if (event.participantCount !== undefined) {
      try {
        fullEvent = await eventsAPI.getById(event.id);
      } catch (error) {
        console.error('Error loading event participants:', error);
      }
    }

    const starStatus = {};
    const pictureStatus = {};

    try {
      const organizerStarGiven = await badgesAPI.checkIfGiven(currentUser.id, fullEvent.organizer.id, fullEvent.id);
      starStatus[fullEvent.organizer.id] = organizerStarGiven;
      const organizerPictureExists = await picturesAPI.checkExists(currentUser.id, fullEvent.organizer.id, fullEvent.id);
      pictureStatus[fullEvent.organizer.id] = organizerPictureExists;
    } catch (error) {
      console.error('Error checking organizer status:', error);
      starStatus[fullEvent.organizer.id] = false;
      pictureStatus[fullEvent.organizer.id] = false;
    }

    if (fullEvent.participants) {
      for (const participant of fullEvent.participants) {
        try {
          const starGiven = await badgesAPI.checkIfGiven(currentUser.id, participant.user.id, fullEvent.id);
          starStatus[participant.user.id] = starGiven;
          const pictureExists = await picturesAPI.checkExists(currentUser.id, participant.user.id, fullEvent.id);
          pictureStatus[participant.user.id] = pictureExists;
        } catch (error) {
          console.error('Error checking participant status:', error);
          starStatus[participant.user.id] = false;
          pictureStatus[participant.user.id] = false;
        }
      }
    }

    setReactDialogEvent(fullEvent);
    setGivenStars(starStatus);
    setExistingPictures(pictureStatus);
    setLoadingGathering(false);
  };

  const handleReactToFollowedEvent = async (communityEvent) => {
    try {
      setLoadingGathering(true);
      setShowReactDialog(true);

      const gatherings = await communityEventsAPI.getGatherings(communityEvent.id);

      if (!gatherings || gatherings.length === 0) {
        alert(t('gatheringsSidebar.alerts.noGatherings'));
        setShowReactDialog(false);
        setLoadingGathering(false);
        return;
      }

      await openReactDialog(gatherings[0], communityEvent.name);
    } catch (error) {
      console.error('Error loading gathering:', error);
      alert(t('gatheringsSidebar.alerts.failedLoadGathering'));
      setShowReactDialog(false);
      setLoadingGathering(false);
    }
  };

  const handleReactToPastEvent = async (event) => {
    try {
      await openReactDialog(event, event.name);
    } catch (error) {
      console.error('Error loading event:', error);
      alert(t('gatheringsSidebar.alerts.failedLoadEvent'));
      setShowReactDialog(false);
    }
  };

  const handleCloseReactDialog = () => {
    setShowReactDialog(false);
    setReactDialogEvent(null);
    setReactDialogTitle('');
    setGivenStars({});
    setExistingPictures({});
  };

  const handleSendStar = (participant) => {
    if (giveawayStars === 0) {
      alert(t('gatheringsSidebar.alerts.noStars'));
      return;
    }
    setSelectedStarRecipient(participant);
    setShowStarModal(true);
  };

  const handleCloseStarModal = () => {
    setShowStarModal(false);
    setSelectedStarRecipient(null);
  };

  const handleSubmitStar = async (message, isAnonymous, isPrivate) => {
    const eventId = reactDialogEvent?.id;

    if (!selectedStarRecipient || !eventId) {
      alert(t('gatheringsSidebar.alerts.missingInfo'));
      return;
    }

    try {
      await badgesAPI.giveBadge(
        selectedStarRecipient.user.id,
        eventId,
        message,
        isAnonymous,
        isPrivate
      );

      if (isAnonymous) {
        alert(t('gatheringsSidebar.alerts.starSentAnonymous', { name: selectedStarRecipient.user.displayName }));
      } else {
        alert(t('gatheringsSidebar.alerts.starSent', { name: selectedStarRecipient.user.displayName }));
      }

      // Reload giveaway stars count
      const count = await badgesAPI.getGiveawayCount(currentUser.id);
      setGiveawayStars(count);

      // Update given stars status
      setGivenStars(prev => ({
        ...prev,
        [selectedStarRecipient.user.id]: true
      }));

      handleCloseStarModal();
    } catch (error) {
      console.error('Error sending star:', error);
      const errorMessage = error.response?.data?.message || error.response?.data || t('gatheringsSidebar.alerts.failedStar');
      alert(errorMessage);
    }
  };

  const handleAddPicture = (participant) => {
    setSelectedParticipant(participant);
    setGroupPhotoEventId(null);
    setShowUploadModal(true);
  };

  const handleAddGroupPhoto = (event) => {
    setSelectedParticipant(null);
    setGroupPhotoEventId(event.id);
    setGroupPhotoEvent(event);
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedParticipant(null);
    setGroupPhotoEventId(null);
    setGroupPhotoEvent(null);
  };


  const handleOpenEventGallery = async (event) => {
    setGalleryEvent(event);
    try {
      let pictures;
      if (event.type === 'followed') {
        pictures = await picturesAPI.getCommunityEventPictures(event.id);
      } else if (event.communityEventId) {
        const [communityPics, eventPics] = await Promise.all([
          picturesAPI.getCommunityEventPictures(event.communityEventId),
          picturesAPI.getEventPictures(event.id)
        ]);
        const seen = new Set();
        pictures = [...communityPics, ...eventPics].filter(p => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
      } else {
        pictures = await picturesAPI.getEventPictures(event.id);
      }
      setEventGalleryPictures(pictures);
      setShowEventGallery(true);
    } catch (error) {
      console.error('Error loading event pictures:', error);
      alert(t('gatheringsSidebar.alerts.failedGallery'));
    }
  };

  const renderPastEventCard = (event) => {
    const eventDateTime = parseUTCDate(event.dateTime);

    return (
      <div key={event.id} className="gathering-card past-event">
        <div className="gathering-header">
          <div className="gathering-title">
            <span className="past-indicator">🕒</span>
            <strong>{event.name}</strong>
          </div>
          <span className="status-badge ended-badge">{t('gatheringsSidebar.badges.ended')}</span>
        </div>

        {event.hashtags && event.hashtags.length > 0 && (
          <div className="gathering-hashtags">
            {event.hashtags.map(tag => (
              <span key={tag} className="gathering-hashtag">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {event.organizer && (
          <DescriptionOrg
            creator={event.organizer}
            creatorDescription={event.description}
            onUserClick={handleUserClick}
          />
        )}

        <div className="gathering-details">
          {event.address && (
            <p className="gathering-address clickable-address" onClick={() => setMapAddress(event.address)}>📍 {event.address}</p>
          )}
          <p className="gathering-date">
            📅 {formatDateLong(event.dateTime, i18n.language)}
            {formatTime(event.dateTime, i18n.language)
              ? ` ${t('gatheringsSidebar.at')} ${formatTime(event.dateTime, i18n.language)}`
              : ` - ${t('gatheringsSidebar.anytime')}`}
          </p>
          <p
            className="gathering-participants clickable"
            onClick={() => showParticipants(event)}
            title={t('gatheringsSidebar.clickToSeeParticipants')}
          >
            👥 {event.participantCount} {t('gatheringsSidebar.people')}
          </p>
          <p className="gallery-link" onClick={() => handleOpenEventGallery(event)}>
            📷 {t('gatheringsSidebar.gallery')}
          </p>
        </div>

        <div className="gathering-actions">
          <button
            className="react-btn"
            onClick={() => handleReactToPastEvent(event)}
          >
            {t('gatheringsSidebar.buttons.react')}
          </button>
          {event.organizer?.id === currentUser.id && (
            <button
              className="add-event-photo-btn"
              onClick={() => handleAddGroupPhoto(event)}
              disabled={!!groupPhotoExists[event.id]}
            >
              {t('gatheringsSidebar.buttons.eventPhoto')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderFollowedEventCard = (event) => {
    // Only use gathering datetime for ended/ongoing — if no gathering created yet, don't show ended
    const displayDateTime = event.autoCreatedEventDateTime || event.dateTime;
    const eventDateTime = displayDateTime ? new Date(displayDateTime) : null;
    const eventEndTime = eventDateTime ? new Date(eventDateTime.getTime() + (2 * 60 * 60 * 1000)) : null;
    const now = new Date();
    const hasEnded = !!event.autoCreatedEventDateTime && eventEndTime < now;
    const isOngoing = !!event.autoCreatedEventDateTime && eventDateTime < now && !hasEnded;
    const canReact = (hasEnded || isOngoing) && event.hasGatherings;

    return (
      <div key={event.id} className="gathering-card followed">
        <div className="gathering-header followed-header">
          <div className="followed-header-row">
            <div className="gathering-title">
              <span className="followed-star">⭐</span>
              <strong>{event.name}</strong>
              {event.createdById === currentUser.id && (
                <span className="creator-badge">{t('gatheringsSidebar.badges.creator')}</span>
              )}
            </div>
            <button
              className="unfollow-btn"
              onClick={() => handleUnfollow(event.id)}
              title={t('gatheringsSidebar.buttons.unfollow')}
            >
              ✕
            </button>
          </div>

          {/* Event Status Badges */}
          <div className="event-status-badges">
            {isOngoing && (
              <span className="status-badge ongoing-badge">{t('gatheringsSidebar.badges.ongoing')}</span>
            )}
            {event.userHasJoinedAutoCreatedEvent && !hasEnded && (
              <span className="status-badge going-badge">{t('gatheringsSidebar.badges.going')}</span>
            )}
            {event.status === 1 && (
              <span className="status-badge approved-badge">{t('gatheringsSidebar.badges.approved')}</span>
            )}
            {event.status === 0 && (
              <span className="status-badge pending-badge">{t('gatheringsSidebar.badges.pendingApproval')}</span>
            )}
            {event.status === -1 && (
              <span className="status-badge danger-badge">{t('gatheringsSidebar.badges.reportedDangerous')}</span>
            )}
            {event.hasGatherings && (
              <span className="status-badge gathering-badge">{t('gatheringsSidebar.badges.hasGatherings')}</span>
            )}
            {hasEnded && (
              <span className="status-badge ended-badge">{t('gatheringsSidebar.badges.ended')}</span>
            )}
            {event.dangerousComment && (
              <span className="status-badge danger-badge">{t('gatheringsSidebar.badges.safetyConcern', { score: `${event.dangerScore > 0 ? '+' : ''}${event.dangerScore}` })}</span>
            )}
          </div>
        </div>

        {event.hashtags && event.hashtags.length > 0 && (
          <div className="gathering-hashtags">
            {event.hashtags.map(tag => (
              <span key={tag} className="gathering-hashtag">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {event.organizer && (
          <DescriptionOrg
            creator={event.organizer}
            creatorDescription={event.description}
            organizer={event.gatheringOrganizer || null}
            organizerDescription={event.gatheringDescription || null}
            onUserClick={handleUserClick}
          />
        )}

        <div className="gathering-details">
          {event.address && (
            <p className="gathering-address clickable-address" onClick={() => setMapAddress(event.address)}>📍 {event.address}</p>
          )}
          {eventDateTime && (
            <p className="gathering-date">
              📅 {formatDateLong(displayDateTime, i18n.language)}
              {formatTime(displayDateTime, i18n.language)
                ? ` ${t('gatheringsSidebar.at')} ${formatTime(displayDateTime, i18n.language)}`
                : ` - ${t('gatheringsSidebar.anytime')}`}
            </p>
          )}
          {event.gatheringParticipantCount > 0 && (
            <p
              className={`gathering-participants${event.autoCreatedEventId ? ' clickable' : ''}`}
              onClick={event.autoCreatedEventId ? () => showFollowedEventParticipants(event) : undefined}
              title={event.autoCreatedEventId ? t('gatheringsSidebar.clickToSeeParticipants') : undefined}
            >
              👥 {event.gatheringParticipantCount} {t('gatheringsSidebar.people')}
            </p>
          )}
          <p className="gallery-link" onClick={() => handleOpenEventGallery(event)}>
            📷 {t('gatheringsSidebar.gallery')}
          </p>
        </div>

        {canReact && (
          <div className="gathering-actions">
            <button
              className="react-btn"
              onClick={() => handleReactToFollowedEvent(event)}
            >
              {t('gatheringsSidebar.buttons.react')}
            </button>
          </div>
        )}
        
      </div>
    );
  };

  if (!currentUser) {
    return null;
  }

  // Combine gatherings, followed events, and past events based on filter
  const combinedEvents = [];

  if (eventFilter === 'both' || eventFilter === 'attending') {
    combinedEvents.push(...myGatherings.map(g => ({ ...g, type: 'gathering' })));
  }

  if (eventFilter === 'both' || eventFilter === 'following') {
    // Exclude PersonalGatherings (eventType 5) from dedup — they are distinct entries
    // with their own description and should show alongside the community event
    const myGatheringIds = new Set(myGatherings.filter(g => g.eventType !== 5).map(g => g.id));
    const dedupedFollowed = followedEvents.filter(f =>
      !f.autoCreatedEventId || !myGatheringIds.has(f.autoCreatedEventId)
    );
    combinedEvents.push(...dedupedFollowed.map(f => ({ ...f, type: 'followed' })));
  }

  if (eventFilter === 'both' || eventFilter === 'past') {
    combinedEvents.push(...pastEvents.map(p => ({ ...p, type: 'past' })));
  }

  // Sort: pending gatherings first, then confirmed/organizing, then followed events, then past events, then declined
  const sortedEvents = combinedEvents.length > 0 ? [...combinedEvents].sort((a, b) => {
    if (a.type === 'gathering' && b.type === 'gathering') {
      const statusA = isOrganizer(a) ? 'organizing' : getParticipantStatus(a);
      const statusB = isOrganizer(b) ? 'organizing' : getParticipantStatus(b);
      const priorityOrder = { pending: 1, organizing: 2, confirmed: 2, declined: 4 };
      return priorityOrder[statusA] - priorityOrder[statusB];
    }

    if (a.type === 'gathering' && b.type === 'followed') {
      const statusA = isOrganizer(a) ? 'organizing' : getParticipantStatus(a);
      // Followed events come after confirmed/organizing but before declined
      if (statusA === 'declined') return 1; // followed before declined
      return -1; // gatherings before followed
    }

    if (a.type === 'followed' && b.type === 'gathering') {
      const statusB = isOrganizer(b) ? 'organizing' : getParticipantStatus(b);
      if (statusB === 'declined') return -1; // followed before declined
      return 1; // gatherings before followed
    }

    // Past events come after both gatherings and followed events
    if (a.type === 'past' || b.type === 'past') {
      if (a.type === b.type) {
        // Both are past events, sort by date (most recent first)
        const dateA = parseUTCDate(a.dateTime);
        const dateB = parseUTCDate(b.dateTime);
        return dateB - dateA;
      }
      // One is past, one is not - past comes last (except before declined gatherings)
      if (a.type === 'past') {
        if (b.type === 'gathering') {
          const statusB = isOrganizer(b) ? 'organizing' : getParticipantStatus(b);
          if (statusB === 'declined') return -1; // past before declined
        }
        return 1; // past after everything else
      }
      if (b.type === 'past') {
        if (a.type === 'gathering') {
          const statusA = isOrganizer(a) ? 'organizing' : getParticipantStatus(a);
          if (statusA === 'declined') return 1; // past before declined
        }
        return -1; // past after everything else
      }
    }

    // Both followed: sort by status (ongoing -> upcoming -> ended)
    const dateTimeA = a.autoCreatedEventDateTime || a.dateTime;
    const dateTimeB = b.autoCreatedEventDateTime || b.dateTime;
    const startA = new Date(dateTimeA);
    const startB = new Date(dateTimeB);
    const endA = new Date(startA.getTime() + (2 * 60 * 60 * 1000));
    const endB = new Date(startB.getTime() + (2 * 60 * 60 * 1000));
    const now = new Date();

    // Determine status for each event
    const getEventStatus = (start, end) => {
      if (end < now) return 'ended';
      if (start < now) return 'ongoing';
      return 'upcoming';
    };

    const statusA = getEventStatus(startA, endA);
    const statusB = getEventStatus(startB, endB);

    // Priority order: ongoing > upcoming > ended
    const statusPriority = { ongoing: 1, upcoming: 2, ended: 3 };

    if (statusA !== statusB) {
      return statusPriority[statusA] - statusPriority[statusB];
    }

    // Within same status, sort by datetime (earliest first)
    return startA - startB;
  }) : [];

  const includeEvent = myGatherings.find(e => e.id === includeEventId);
  const includeExcludeUserIds = [
    includeEventFull?.organizer?.id ?? includeEvent?.organizer?.id,
    ...(includeEventFull?.participants?.map(p => p.user.id) || [])
  ].filter(Boolean);

  return (
    <div className={`gatherings-sidebar-container ${isMobile && sidebarOpen ? 'mobile-open' : ''} ${!isMobile && !sidebarOpen ? 'desktop-hidden' : ''}`}>
      <div className={`gatherings-sidebar ${isMobile ? (sidebarOpen ? 'mobile-open' : '') : (sidebarOpen ? '' : 'hidden')}`}>
        <h3>{t('gatheringsSidebar.title')}</h3>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${eventFilter === 'both' ? 'active' : ''}`}
            onClick={() => setEventFilter('both')}
          >
            {t('gatheringsSidebar.filters.all')}
          </button>
          <button
            className={`filter-btn ${eventFilter === 'attending' ? 'active' : ''}`}
            onClick={() => setEventFilter('attending')}
          >
            {t('gatheringsSidebar.filters.attending')}
          </button>
          <button
            className={`filter-btn ${eventFilter === 'following' ? 'active' : ''}`}
            onClick={() => setEventFilter('following')}
          >
            {t('gatheringsSidebar.filters.following')}
          </button>
          <button
            className={`filter-btn ${eventFilter === 'past' ? 'active' : ''}`}
            onClick={() => setEventFilter('past')}
          >
            {t('gatheringsSidebar.filters.past')}
          </button>
        </div>

        {loading ? (
          <p className="no-gatherings">{t('gatheringsSidebar.loading')}</p>
        ) : combinedEvents.length === 0 ? (
          <p className="no-gatherings">{t('gatheringsSidebar.noEvents', { count: SIDEBAR_PAST_GATHERINGS_DAYS })}</p>
        ) : (
          <div className="gatherings-list">
            {sortedEvents.map(event => {
          // Render past events
          if (event.type === 'past') {
            return renderPastEventCard(event);
          }

          // Render followed events differently
          if (event.type === 'followed') {
            return renderFollowedEventCard(event);
          }

          // Render gathering events
          const status = isOrganizer(event) ? 'organizing' : getParticipantStatus(event);
          const organizing = isOrganizer(event);
          const isDeclined = status === 'declined';
          const ongoing = isOngoing(event);

          return (
            <div
              key={event.id}
              className={`gathering-card ${status} ${isDeclined ? 'compact' : ''} ${ongoing ? 'ongoing' : ''}`}
            >
              <div className="gathering-header">
                <div className="gathering-title">
                  {status === 'pending' && !organizing && (
                    <span className="pending-indicator">★</span>
                  )}
                  <strong>{event.name}</strong>
                </div>
                {ongoing && (
                  <span className="ongoing-badge">{t('gatheringsSidebar.badges.ongoing')}</span>
                )}
                {organizing && !ongoing && (
                  <span className="organizer-badge">{t('gatheringsSidebar.badges.organizer')}</span>
                )}
                {isDeclined && (
                  <span className="status-badge declined-badge compact-badge">
                    {t('gatheringsSidebar.badges.declined')}
                  </span>
                )}
              </div>

              {!isDeclined && (
                <>
                  {event.hashtags && event.hashtags.length > 0 && (
                    <div className="gathering-hashtags">
                      {event.hashtags.map(tag => (
                        <span key={tag} className="gathering-hashtag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {event.organizer && (
                    <DescriptionOrg
                      creator={event.communityEventOrganizer || event.organizer}
                      creatorDescription={event.communityEventOrganizer ? event.communityEventDescription : event.description}
                      organizer={event.communityEventOrganizer ? event.organizer : null}
                      organizerDescription={event.communityEventOrganizer ? event.description : null}
                      onUserClick={handleUserClick}
                    />
                  )}

                  <div className="gathering-details">
                    {event.address && (
                      <p className="gathering-address clickable-address" onClick={() => setMapAddress(event.address)}>
                        📍 {event.address}
                      </p>
                    )}
                    <p className="gathering-date">
                      📅 {formatDateLong(event.dateTime, i18n.language)}
                      {formatTime(event.dateTime, i18n.language)
                        ? ` ${t('gatheringsSidebar.at')} ${formatTime(event.dateTime, i18n.language)}`
                        : ` - ${t('gatheringsSidebar.anytime')}`}
                    </p>
                    <p
                      className="gathering-participants clickable"
                      onClick={() => showParticipants(event)}
                      title={t('gatheringsSidebar.clickToSeeParticipants')}
                    >
                      👥 {event.participantCount} {t('gatheringsSidebar.people')}
                    </p>
                    <p
                    className="gallery-link"
                    onClick={() => handleOpenEventGallery(event)}
                  >
                    📷 {t('gatheringsSidebar.gallery')}
                  </p>
                  </div>

                  {organizing && (
                    <div className="gathering-actions">
                      {(isUpcoming(event) || ongoing) && (
                        <button
                          className="include-btn"
                          onClick={() => openIncludeDialog(event.id)}
                        >
                          {t('gatheringsSidebar.buttons.include')}
                        </button>
                      )}
                      {ongoing && (
                        <button
                          className="add-event-photo-btn"
                          onClick={() => handleAddGroupPhoto(event)}
                          disabled={!!groupPhotoExists[event.id]}
                        >
                          {t('gatheringsSidebar.buttons.eventPhoto')}
                        </button>
                      )}
                      {ongoing && (
                        <button
                          className="end-btn"
                          onClick={() => handleEndGathering(event.id)}
                        >
                          {t('gatheringsSidebar.buttons.end')}
                        </button>
                      )}
                    </div>
                  )}

                  {!organizing && (
                    <div className="gathering-actions">
                      {status === 'pending' && (
                        <>
                          <button
                            className="confirm-btn"
                            onClick={() => handleConfirm(event.id)}
                          >
                            {t('gatheringsSidebar.buttons.confirm')}
                          </button>
                          <button
                            className="decline-btn"
                            onClick={() => handleDecline(event.id)}
                          >
                            {t('gatheringsSidebar.buttons.decline')}
                          </button>
                        </>
                      )}
                      {status === 'confirmed' && (
                        <>
                          <div className="status-badge confirmed-badge">
                            {t('gatheringsSidebar.badges.confirmed')}
                          </div>
                          {isUpcoming(event) && (
                            <button
                              className="leave-btn"
                              onClick={() => handleLeaveEvent(event.id)}
                            >
                              {t('gatheringsSidebar.buttons.leave')}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {ongoing && (organizing || status === 'confirmed') && (
                    <div className="gathering-actions">
                      <button
                        className="react-btn"
                        onClick={() => handleReactToPastEvent(event)}
                      >
                        {t('gatheringsSidebar.buttons.react')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
          </div>
        )}
      </div>

      <button
        className={`sidebar-toggle-btn ${sidebarOpen ? 'sidebar-open' : ''}`}
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {sidebarOpen ? '×' : '☰'}
        {isMobile && !sidebarOpen && hasPendingInvites && (
          <span className="sidebar-pending-badge">✱</span>
        )}
      </button>

      {selectedEvent && (
        <ParticipantsDialog
          event={selectedEvent}
          currentUser={currentUser}
          isPast={isEventPast(selectedEvent.dateTime, selectedEvent.isEnded)}
          onClose={closeParticipantsDialog}
          onUserClick={handleUserClick}
        />
      )}

      {/* Include Dialog */}
      <InviteFriendsDialog
        isOpen={showIncludeDialog}
        onClose={closeIncludeDialog}
        selectedFriends={selectedUsers}
        onToggle={handleUserToggle}
        currentUser={currentUser}
        excludeUserIds={includeExcludeUserIds}
        footer={
          <div className="include-dialog-footer">
            <button className="cancel-btn" onClick={closeIncludeDialog}>{t('common.cancel')}</button>
            <button
              className="add-btn"
              onClick={handleAddParticipants}
              disabled={addingParticipants || selectedUsers.length === 0}
            >
              {addingParticipants ? t('gatheringsSidebar.buttons.adding') : t('gatheringsSidebar.buttons.add', { count: selectedUsers.length })}
            </button>
          </div>
        }
      />

      {/* React Dialog (shared for followed, past, and ongoing events) */}
      {showReactDialog && (
        <ReactDialog
          event={reactDialogEvent}
          title={reactDialogTitle}
          currentUser={currentUser}
          giveawayStars={giveawayStars}
          givenStars={givenStars}
          existingPictures={existingPictures}
          loading={loadingGathering}
          onClose={handleCloseReactDialog}
          onSendStar={handleSendStar}
          onAddPicture={handleAddPicture}
          onUserClick={setSelectedUserId}
        />
      )}

      {/* Star Modal */}
      {showStarModal && selectedStarRecipient && (
        <SendStarDialog
          recipient={selectedStarRecipient}
          giveawayStars={giveawayStars}
          onClose={handleCloseStarModal}
          onSubmit={handleSubmitStar}
        />
      )}

      {/* Event Gallery */}
      {showEventGallery && galleryEvent && (
        <EventGalleryModal
          pictures={eventGalleryPictures}
          event={galleryEvent}
          onClose={() => setShowEventGallery(false)}
        />
      )}

      {/* Picture Upload Modal */}
      {showUploadModal && (selectedParticipant || groupPhotoEventId) && (
        <PictureUploadModal
          currentUser={currentUser}
          selectedParticipant={selectedParticipant}
          event={groupPhotoEventId ? groupPhotoEvent : reactDialogEvent}
          isGroupPhoto={!!groupPhotoEventId}
          onClose={handleCloseUploadModal}
          onUploaded={({ type, eventId, userId }) => {
            if (type === 'group') setGroupPhotoExists(prev => ({ ...prev, [eventId]: true }));
            else setExistingPictures(prev => ({ ...prev, [userId]: true }));
          }}
        />
      )}

      {/* User Detail Modal */}
      {selectedUserId && createPortal(
        <UserDetail
          userId={selectedUserId}
          onClose={handleCloseUserDetail}
        />,
        document.body
      )}

      {mapAddress && createPortal(
        <div className="map-dialog-overlay" onClick={() => setMapAddress(null)}>
          <div className="map-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-dialog-header">
              <span>📍 {mapAddress}</span>
              <button className="map-dialog-close" onClick={() => setMapAddress(null)}>×</button>
            </div>
            <iframe
              title="Event Location"
              className="map-dialog-iframe"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapAddress)}&output=embed`}
              allowFullScreen
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default GatheringsSidebar;
